function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/web.dom.iterable.js";
import React, { useEffect, useRef, useState } from 'react';
import { bool, func, instanceOf, node, number, oneOf, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames/bind';
import { applyInlineOffsetMargin, getScrollPosition, getPosition, isRefInViewport, isScrolledPastContent, isScrolledPastOffset, isScrollingDown } from '../helpers';
import styles from './sticky.module.scss';
import { StickyContext } from './StickyContext';
const cx = classNames.bind(styles);
const Sticky = _ref => {
  var _stickyRef$current;
  let {
    animation,
    boxShadow,
    children,
    className,
    forwardRef,
    offsetMargin,
    offsetScroll,
    position,
    stickyType,
    stickyByDefault,
    visibleByDefault,
    zIndex
  } = _ref;
  const [isSticky, setIsSticky] = useState(stickyByDefault);
  const [isVisible, setIsVisible] = useState(visibleByDefault);
  const [offsetMarginValue, setOffsetMarginValue] = useState(offsetMargin);
  useEffect(() => {
    setOffsetMarginValue(offsetMargin);
  }, [offsetMargin]);
  const stickyRef = useRef(null);
  const contentPosition = useRef({
    bottom: 0,
    top: 0
  });
  const lastScrollPosition = useRef({
    last: 0
  });
  const shouldDisplayBasic = () => {
    const shouldDisplay = isScrolledPastOffset(offsetScroll, window);
    if (isVisible !== shouldDisplay) {
      setIsVisible(shouldDisplay);
    }
  };
  const shouldDisplayRef = () => {
    if (!(forwardRef !== null && forwardRef !== void 0 && forwardRef.current)) {
      return;
    }
    const {
      offsetHeight
    } = forwardRef === null || forwardRef === void 0 ? void 0 : forwardRef.current;
    const forwardRefRect = forwardRef === null || forwardRef === void 0 ? void 0 : forwardRef.current.getBoundingClientRect();
    const stickyRefRect = stickyRef === null || stickyRef === void 0 ? void 0 : stickyRef.current.getBoundingClientRect();
    const isForwardRefInViewport = isRefInViewport({
      offsetScroll,
      refHeight: offsetHeight,
      refRect: forwardRefRect,
      windowObj: window
    });
    if (isVisible !== !isForwardRefInViewport) {
      setIsVisible(!isForwardRefInViewport);
    }
  };
  const shouldDisplayOnScrollUp = () => {
    // saves top and bottom position of the content to be made sticky
    const {
      bottom,
      top
    } = (contentPosition === null || contentPosition === void 0 ? void 0 : contentPosition.current) || {};
    if (!bottom && !top) {
      const {
        bottomOffset,
        topOffset
      } = getPosition(stickyRef);
      contentPosition.current.top = topOffset;
      contentPosition.current.bottom = bottomOffset;
    }

    // get current scroll position
    const scrollPosition = getScrollPosition(window);
    if (isSticky) {
      if (isScrollingDown(scrollPosition, lastScrollPosition.current.last)) {
        setOffsetMarginValue(0);
        setIsVisible(false);
      } else {
        setOffsetMarginValue(offsetMargin);
        setIsVisible(true);
      }
      if (!isScrolledPastContent(window, top)) {
        setIsSticky(false);
      }
    } else if (isScrolledPastContent(window, bottom)) {
      if (isScrollingDown(scrollPosition, lastScrollPosition.current.last)) {
        // hide content
        setIsVisible(false);
        setOffsetMarginValue(0);
      } else {
        // show content
        setOffsetMarginValue(offsetMargin);
        setIsSticky(true);
        setIsVisible(true);
      }
    }
    lastScrollPosition.current.last = scrollPosition <= 0 ? 0 : scrollPosition;
  };
  const handleScroll = () => {
    switch (stickyType) {
      case 'basic':
        shouldDisplayBasic();
        break;
      case 'forwardRef':
        shouldDisplayRef();
        break;
      case 'showOnScrollUp':
        shouldDisplayOnScrollUp();
        break;
      default:
        shouldDisplayBasic();
    }
  };
  useEffect(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky, isVisible]);
  const stickyStyles = _objectSpread({
    zIndex
  }, applyInlineOffsetMargin(animation, offsetMarginValue, position, stickyType, isVisible));
  const stickyClasses = classNames(className, `sui-${position}-0`, {
    'sui-fixed': isSticky,
    'sui-hidden': !isVisible && !animation,
    '!sui-block': isVisible && !animation,
    /* No tailwind classes currently exist to support the legacy animations */
    // makes the content to slide in from top/bottom while showing and hiding
    [cx('sticky__slide--fixed')]: isSticky && animation === 'slide',
    [cx('sticky__slide--bottom-hide')]: !isVisible && animation === 'slide' && position === 'bottom',
    [cx('sticky__slide--top-hide')]: !isVisible && animation === 'slide' && position === 'top',
    // makes the content to zoom/scale while showing and hiding
    [cx('sticky__scale--fixed')]: isSticky && animation === 'scale',
    [cx('sticky__scale--show')]: isSticky && animation === 'scale',
    [cx('sticky__scale--hide')]: !isVisible && animation === 'scale',
    [cx('sticky__shadow--bottom')]: boxShadow && position === 'bottom',
    'sui-shadow-lg': boxShadow && position === 'top'
  });
  const wrapperStyle = {
    minHeight: stickyType === 'showOnScrollUp' ? stickyRef === null || stickyRef === void 0 ? void 0 : (_stickyRef$current = stickyRef.current) === null || _stickyRef$current === void 0 ? void 0 : _stickyRef$current.offsetHeight : 0
  };
  return /*#__PURE__*/React.createElement("div", {
    style: wrapperStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: stickyClasses,
    ref: stickyRef,
    style: stickyStyles
  }, /*#__PURE__*/React.createElement(StickyContext.Provider, {
    value: {
      isVisible
    }
  }, children)));
};
Sticky.displayName = 'Sticky';
Sticky.propTypes = {
  animation: oneOf(['scale', 'slide']),
  boxShadow: bool,
  children: node,
  className: string,
  // avoid propType error by allowing undefined prop type on ssr only
  forwardRef: oneOfType([func, shape({
    current: instanceOf(typeof Element === 'undefined' ? function () {} : Element)
  })]),
  offsetMargin: number,
  offsetScroll: number,
  position: oneOf(['bottom', 'top']).isRequired,
  stickyByDefault: bool,
  stickyType: oneOf(['basic', 'forwardRef', 'showOnScrollUp']),
  visibleByDefault: bool,
  zIndex: number
};
Sticky.defaultProps = {
  animation: null,
  boxShadow: false,
  children: null,
  className: '',
  forwardRef: null,
  offsetMargin: 0,
  offsetScroll: 0,
  stickyByDefault: false,
  stickyType: 'basic',
  visibleByDefault: false,
  zIndex: 899
};
export { Sticky };
//# sourceMappingURL=Sticky.js.map